import { type ContractApplicationEntry } from '@access-cms/api-client';

import Dropdown from '../../../../components/Dropdown';

import './ApplicationPicker.css';

export const APPLICATION_PICKER_LABEL = 'Choose an application to take action on';
export const APPLICATION_PICKER_PLACEHOLDER = 'Select an application';

/** Domain acronyms that must not be title-cased in status labels. */
// const STATUS_ACRONYMS = new Set(['COR', 'FNMS', 'VAS', 'EAPP']);

// function formatApplicationStatus(status: string): string {
//   return status
//     .split('_')
//     .map((word) => (STATUS_ACRONYMS.has(word) ? word : word.charAt(0) + word.slice(1).toLowerCase()))
//     .join(' ');
// }

/**
 * One human-readable line per entry: contract name (number) — status.
 * `contract` is loosely typed on the client, so fields are accessed defensively.
 */
function entryLabel(contractId: string, entry: ContractApplicationEntry): string {
  const contract = entry.contract ?? {};
  const name =
    typeof contract.contractName === 'string' && contract.contractName
      ? contract.contractName
      : `Contract ${contractId}`;
  const number =
    typeof contract.contractNumber === 'string' && contract.contractNumber ? ` (${contract.contractNumber})` : '';
  const company =
    typeof contract.companyName === 'string' && contract.companyName
      ? contract.companyName
      : typeof contract.company === 'string' && contract.company
      ? contract.company
      : 'Not provided';
  const laborCategory = entry.laborCategory?.categoryName || 'Not provided';

  // TODO: prepped for displaying status if needed
  // let status = 'Not Started';
  // if (entry.application) {
  //   status = entry.application.applicationStatus
  //     ? formatApplicationStatus(entry.application.applicationStatus)
  //     : 'In Progress';
  // }

  // return `Contract: ${name}${number}\nCompany: ${company}\nLabor Category: ${laborCategory}\nStatus: ${status}`;
  return `Contract: ${name}${number}\nCompany: ${company}\nLabor Category: ${laborCategory}`;
}

function renderApplicationOption(option: { label: string }) {
  return (
    <span className='application-picker-option'>
      {option.label.split('\n').map((line, index) => {
        const separatorIndex = line.indexOf(':');
        const label = separatorIndex >= 0 ? line.slice(0, separatorIndex + 1) : '';
        const value = separatorIndex >= 0 ? line.slice(separatorIndex + 1) : line;

        return (
          <span
            key={`${line}-${index}`}
            className='application-picker-option__line'>
            {label && <strong>{label}</strong>}
            {value}
          </span>
        );
      })}
    </span>
  );
}

interface ApplicationPickerProps {
  /**
   * Per-contract application entries to choose between, keyed by contractId
   * (the `applications` record from GET /api/v1/applications/me)
   */
  applications: Record<string, ContractApplicationEntry>;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
  /**
   * Called with the chosen contractId
   */
  onSelect: (contractId: string) => void;
  /**
   * The currently selected contractId, or null when none is chosen yet
   */
  selectedContractId: string | null;
}

/**
 * Dropdown for choosing which application/contract to act on.
 *
 * Rendered only when the user has more than one contract/application; a single
 * entry is auto-selected upstream and does not need a picker.
 */
function ApplicationPicker({
  applications,
  'data-testid': dataTestId,
  onSelect,
  selectedContractId,
}: ApplicationPickerProps) {
  const baseTestId = dataTestId ?? 'application-picker';
  const entries = Object.entries(applications);
  const isLocked = entries.length === 1;

  const options = entries.map(([contractId, entry]) => ({
    label: entryLabel(contractId, entry),
    value: contractId,
  }));

  return (
    <div
      className='ds-u-margin-y--3'
      data-testid={baseTestId}>
      <Dropdown
        required
        className='application-picker-dropdown'
        data-testid={`${baseTestId}-dropdown`}
        disabled={isLocked}
        label={APPLICATION_PICKER_LABEL}
        name='application-picker'
        options={options}
        placeholder={APPLICATION_PICKER_PLACEHOLDER}
        renderOption={renderApplicationOption}
        value={selectedContractId ?? undefined}
        onChange={onSelect}
      />
    </div>
  );
}

export default ApplicationPicker;
